import React from 'react';
import type { PropertyAnalysisResult } from '../../types/calculations';
import { SectionTitle, GoldBadge } from '../Luxury';
import { Brain, TrendingUp, Users, ShieldCheck, Database } from 'lucide-react';

interface Props {
  data: PropertyAnalysisResult;
}

const getGradeColor = (grade: string): string => {
  if (grade.startsWith('A')) return 'text-emerald-400';
  if (grade.startsWith('B')) return 'text-blue-400';
  if (grade.startsWith('C')) return 'text-amber-400';
  if (grade.startsWith('D')) return 'text-orange-400';
  return 'text-red-400';
};

const getGradeBg = (grade: string): string => {
  if (grade.startsWith('A')) return 'bg-emerald-500/20 border-emerald-500/40';
  if (grade.startsWith('B')) return 'bg-blue-500/20 border-blue-500/40';
  if (grade.startsWith('C')) return 'bg-amber-500/20 border-amber-500/40';
  if (grade.startsWith('D')) return 'bg-orange-500/20 border-orange-500/40';
  return 'bg-red-500/20 border-red-500/40';
};

const getConfidenceBadge = (confidence: string): { variant: 'ssot' | 'gold' | 'off-market'; label: string } => {
  switch (confidence.toLowerCase()) {
    case 'high': return { variant: 'ssot', label: 'High Confidence' };
    case 'medium': return { variant: 'gold', label: 'Medium Confidence' };
    default: return { variant: 'off-market', label: 'Low Confidence' };
  }
};

const getBarColor = (label: string): string => {
  switch (label) {
    case 'financial_strength': return 'bg-emerald-500';
    case 'market_momentum': return 'bg-blue-500';
    case 'demographic_risk': return 'bg-purple-500';
    default: return 'bg-slate-500';
  }
};

const getBarLabel = (key: string): string => {
  switch (key) {
    case 'financial_strength': return 'Financial Strength';
    case 'market_momentum': return 'Market Momentum';
    case 'demographic_risk': return 'Demographic Risk';
    default: return key;
  }
};

const getBarIcon = (key: string) => {
  switch (key) {
    case 'financial_strength': return <ShieldCheck className="w-4 h-4" />;
    case 'market_momentum': return <TrendingUp className="w-4 h-4" />;
    case 'demographic_risk': return <Users className="w-4 h-4" />;
    default: return null;
  }
};

function buildVerdictLine(intel: { composite_score: number; letter_grade: string }): string {
  const score = intel.composite_score;
  const action = score >= 70 ? 'Strong buy'
    : score >= 55 ? 'Conditional buy'
    : score >= 40 ? 'Proceed with caution'
    : 'High risk';
  return `${action} -- ${score}/100 composite score.`;
}

function buildActionBullets(intel: {
  financial_strength: { grade: string; score: number };
  market_momentum: { grade: string; score: number };
  demographic_risk: { grade: string; score: number };
}): string[] {
  return [
    `Financial Strength: ${intel.financial_strength.grade} (${Math.round(intel.financial_strength.score)}/100)`,
    `Market Momentum: ${intel.market_momentum.grade} (${Math.round(intel.market_momentum.score)}/100)`,
    `Demographic Risk: ${intel.demographic_risk.grade} (${Math.round(intel.demographic_risk.score)}/100)`,
  ];
}

export const InvestmentIntelligenceCard: React.FC<Props> = ({ data }) => {
  const intel = data.investment_intelligence;

  if (!intel) {
    return (
      <div className="bg-charcoal rounded-xl border border-slate-dark p-6">
        <SectionTitle icon={<Brain className="w-5 h-5" />}>Investment Intelligence</SectionTitle>
        <p className="text-sm text-stone-400 text-center py-8">
          Investment intelligence data not available for this property.
        </p>
      </div>
    );
  }

  const confidence = getConfidenceBadge(intel.confidence);
  const subScores = [
    { key: 'financial_strength', data: intel.financial_strength },
    { key: 'market_momentum', data: intel.market_momentum },
    { key: 'demographic_risk', data: intel.demographic_risk },
  ];

  return (
    <div className="bg-charcoal rounded-xl border border-slate-dark p-6">
      {/* Header — title only, badge moved to score context bar below */}
      <div className="flex items-center justify-between mb-6">
        <SectionTitle icon={<Brain className="w-5 h-5" />}>Investment Intelligence</SectionTitle>
      </div>

      {/* Verdict — the "so what" */}
      <div className="mb-6">
        <p className="text-base font-semibold text-ivory leading-relaxed">
          {buildVerdictLine(intel)}
        </p>
      </div>

      {/* Actionable sub-score summary */}
      <ul className="space-y-2 mb-6">
        {buildActionBullets(intel).map((bullet, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm text-ivory/80 leading-relaxed">
            <span className="text-gold mt-0.5 flex-shrink-0">&#x2022;</span>
            <span>{bullet}</span>
          </li>
        ))}
      </ul>

      {/* Composite score — secondary context */}
      <div className="flex items-center gap-4 mb-6 py-3 px-4 bg-graphite rounded-lg border border-slate-dark">
        <div className={`w-12 h-12 rounded-full border-2 flex items-center justify-center ${getGradeBg(intel.letter_grade)}`}>
          <span className="text-lg font-bold font-mono text-ivory">{intel.composite_score}</span>
        </div>
        <div>
          <span className={`text-xl font-bold font-mono ${getGradeColor(intel.letter_grade)}`}>{intel.letter_grade}</span>
          <span className="text-sm text-stone-400 ml-2">Composite Grade</span>
        </div>
        <div className="ml-auto">
          <GoldBadge variant={confidence.variant}>{confidence.label}</GoldBadge>
        </div>
      </div>

      {/* Sub-score Bars */}
      <div className="space-y-4">
        {subScores.map(({ key, data: sub }) => (
          <div key={key}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2 text-sm text-stone-300">
                {getBarIcon(key)}
                <span>{getBarLabel(key)}</span>
                <span className="text-sm text-stone-400">({(sub.weight * 100).toFixed(0)}%)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-semibold ${getGradeColor(sub.grade)}`}>{sub.grade}</span>
                <span className="text-sm font-mono text-ivory">{sub.score.toFixed(0)}</span>
              </div>
            </div>
            <div className="h-3 bg-graphite rounded-full overflow-hidden border border-slate-dark">
              <div
                className={`h-full rounded-full transition-all duration-500 ${getBarColor(key)}`}
                style={{ width: `${Math.min(sub.score, 100)}%` }}
              />
            </div>
            {/* Component breakdown */}
            {sub.components && Object.keys(sub.components).length > 0 && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                {Object.entries(sub.components).map(([name, value]) => (
                  <span key={name} className="text-sm text-stone-400">
                    {name.replace(/_/g, ' ')}: <span className="font-mono text-stone-400">{typeof value === 'number' ? value.toFixed(1) : value}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Data Sources */}
      {intel.data_sources && intel.data_sources.length > 0 && (
        <div className="mt-6 pt-4 border-t border-slate-dark">
          <div className="flex items-center gap-2 text-sm text-stone-400">
            <Database className="w-3.5 h-3.5" />
            <span>Sources: {intel.data_sources.join(', ')}</span>
          </div>
        </div>
      )}
    </div>
  );
};
